import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import OtpInput from "react-otp-input";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  setStep,
  setSavedIds,
  setServiceability,
  addStepsData,
} from "app/lib/store/slices/steps/loanTypesSteps";

import { addStepsData as addIndividualIncome } from "app/lib/store/slices/steps/incomeDetails";
import { addStepsData as addJointIncome } from "app/lib/store/slices/steps/jointIncomeDetails";
import { addJointApplicationData } from "app/lib/store/slices/steps/loanTypesSteps";
import { RootState } from "app/lib/store/store";
import toast from "react-hot-toast";
import Loader from "app/components/common/Loader";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import SubTitle from "app/components/common/typography/SubTitle";
import { useGetServiceability } from "app/hooks/application/useGetServiceability";
import { maskPhoneNumber } from "app/utils/index";

import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { useVerifyOtp } from "app/hooks/applicant/useVerifyOtp";
import { useResendOtp } from "app/hooks/applicant/useResendOtp";
import { useUpdateApplicationStatus } from "app/hooks/applicant/useUpdateApplicationStatus";
import { handleApiError } from "app/utils/handleApiError";
import { normalizeSavedIncomes } from "app/utils/normalizeIncome";

import { addStepsData as PersonalAddStepsData } from "app/lib/store/slices/steps/personalDetails";
import {
  extractAssetsLiabilitiesSteps,
  extractEmploymentSteps,
  extractJointPersonalInfoSteps,
  extractPersonalInfoSteps,
  jointExtractAssetLiabilitySteps,
  jointExtractEmploymentSteps,
} from "app/utils/preSavedDataHelperFunctions";
import { addStepsData as employmentAddStepsData } from "app/lib/store/slices/steps/employmentDetails";
import { addStepsData as assetLiabilityAddStepsData } from "app/lib/store/slices/steps/assetsLiabilities";
import { addStepsData as incomeAddStepsData } from "app/lib/store/slices/steps/incomeDetails";
import { addStepsData as JointPersonalAddStepsData } from "app/lib/store/slices/steps/jointpersonalDetails";
import { addStepsData as jointEmploymentAddStepsData } from "app/lib/store/slices/steps/jointemploymentDetails";
import { addStepsData as jointIncomeAddStepsData } from "app/lib/store/slices/steps/jointIncomeDetails";
import { addStepsData as jointAssetLiabilityAddStepsData } from "app/lib/store/slices/steps/jointAssetsLiabilities";
interface FormData {
  otp: string;
}

const OTP: React.FC = () => {
  const dispatch = useDispatch();
  const [timer, setTimer] = useState<number>(0);
  const [isTimerVisible, setIsTimerVisible] = useState<boolean>(false);
  const { applicants, ...remaingData } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const applicationData = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const { loanApplication, vehicleDetail, ...othersData } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );

  const isRefinance = remaingData?.vehicleDetail?.purchaseFrom === "Refinance";
  const JointapplicationData = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const applicantsSavedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );

  const {
    data: serviceabilityData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetServiceability(applicantsSavedIds?.loanApplicationID ?? "");

  const {
    mutate: statueMutate,
    status: statusUpdateStatus,
    isPending: statusIsPending,
    isSuccess: statusIsSuccess,
    isError: statusIsError,
    error: statusError,
  } = useUpdateApplicationStatus();

  const { mutate, isPending } = useVerifyOtp();
  const { mutate: ResendOTPMutate, isPending: resedisPending } = useResendOtp();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      otp: "",
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(
        setSavedIds({
          ...applicantsSavedIds,
          applicationStatus: "declined",
        })
      );
      dispatch(setStep(isRefinance ? 18 : 17));

      statueMutate({
        url: `/loan-application/${applicantsSavedIds?.loanApplicationID}/status`,
        data: {
          applicationStatus: "declined",
        },
      });
    }
  }, [isError, error]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isTimerVisible && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isTimerVisible, timer]);

  const onSubmit = (data: FormData) => {
    const { otp } = data;
    if (applicantsSavedIds?.applicantID) {
      mutate(
        { id: applicantsSavedIds?.applicantID, otp: parseInt(otp) },
        {
          onSuccess: (data: any) => {
            const loanApplicationData = data?.loanApplication;
            // call the API
            refetch();
            if (loanApplicationData && loanApplicationData) {
              dispatch(
                setSavedIds({
                  ...applicantsSavedIds,
                  applicationReferenceNumber:
                    data?.loanApplication?.applicationReferenceNumber,
                })
              );
            }

            if (loanApplicationData.applicationType === "individual") {
              const applicantsData = loanApplicationData?.applicants[0];
              const incomeData = applicantsData?.incomes ?? [];
              const expensesData = applicantsData?.expenses ?? [];

              if (applicantsData) {
                dispatch(
                  PersonalAddStepsData(extractPersonalInfoSteps(applicantsData))
                );
              }

              if (applicantsData?.employment) {
                dispatch(
                  employmentAddStepsData(
                    extractEmploymentSteps(applicantsData?.employment)
                  )
                );
              }
              if (incomeData) {
                const formattedIncomesData = normalizeSavedIncomes(incomeData);
                dispatch(incomeAddStepsData({ income: formattedIncomesData }));
              }

              dispatch(
                assetLiabilityAddStepsData(
                  extractAssetsLiabilitiesSteps(applicantsData)
                )
              );

              dispatch(
                setSavedIds({
                  ...applicantsSavedIds,
                  bankDocLink: loanApplicationData?.applicants[0]?.linkUrl,
                })
              );

              // update the income

              const formattedIncomes = normalizeSavedIncomes(incomeData);
              if (formattedIncomes && formattedIncomes?.length > 0) {
                dispatch(addIndividualIncome({ income: formattedIncomes }));
              }

              dispatch(
                addStepsData({
                  ...othersData,
                  loanApplication: {
                    ...loanApplication,
                    id: loanApplication?.id,
                  },
                  vehicleDetail: {
                    ...vehicleDetail,
                    id: loanApplicationData?.vehicleDetail?.id,
                  },
                  applicants: {
                    ...othersData.applicants,
                    id: loanApplicationData?.applicants[0]?.id,
                    incomes: othersData?.applicants?.incomes?.map(
                      (item: any, index: number) => ({
                        ...item,
                        id:
                          loanApplicationData?.applicants[0]?.incomes?.[index]
                            ?.id ?? item.id,
                      })
                    ),
                    expenses: expensesData?.map((item: any) => ({
                      id: item.id,
                      type: item.type,
                      amount: Number(item.amount),
                    })),
                    employment: {
                      ...othersData?.applicants?.employment,
                      id: loanApplicationData?.applicants[0]?.employment?.id,
                    },
                    liabilities: {
                      ...othersData?.applicants?.liabilities,
                      id: loanApplicationData?.applicants[0]?.liabilities?.id,
                    },
                  },
                })
              );
            } else if (loanApplicationData.applicationType === "joint") {
              dispatch(
                addStepsData({
                  ...othersData,
                  loanApplication: {
                    ...loanApplication,
                    id: loanApplication?.id,
                  },
                  vehicleDetail: {
                    ...vehicleDetail,
                    id: loanApplicationData?.vehicleDetail?.id,
                  },
                })
              );

              const sortedApplicants = [
                ...loanApplicationData?.applicants?.filter(
                  (a: any) => a.isPrimary
                ),
                ...loanApplicationData?.applicants?.filter(
                  (a: any) => !a.isPrimary
                ),
              ];
              const mainApplicant = sortedApplicants?.[0];
              const childApplicant = sortedApplicants?.[1];
              const yourFormattedIncomes = normalizeSavedIncomes(
                mainApplicant?.incomes ?? []
              );
              const coFormattedIncomes = normalizeSavedIncomes(
                childApplicant?.incomes ?? []
              );
              const yourExpensesData = mainApplicant?.expenses ?? [];
              const coExpensesData = childApplicant?.expenses ?? [];

              if (mainApplicant && childApplicant) {
                dispatch(
                  JointPersonalAddStepsData(
                    extractJointPersonalInfoSteps(mainApplicant, childApplicant)
                  )
                );
                dispatch(
                  jointAssetLiabilityAddStepsData(
                    jointExtractAssetLiabilitySteps(
                      mainApplicant,
                      childApplicant
                    )
                  )
                );
              }
              if (mainApplicant?.employment && childApplicant?.employment) {
                dispatch(
                  jointEmploymentAddStepsData(
                    jointExtractEmploymentSteps(
                      mainApplicant?.employment,
                      childApplicant?.employment
                    )
                  )
                );
              }

              if (
                yourFormattedIncomes &&
                yourFormattedIncomes?.length > 0 &&
                coFormattedIncomes &&
                coFormattedIncomes.length > 0
              ) {
                const savedData = {
                  yourDetails: yourFormattedIncomes,
                  coBorrowerDetails: coFormattedIncomes,
                };
                dispatch(jointIncomeAddStepsData({ income: savedData }));

                dispatch(addJointIncome({ income: savedData }));
              }

              dispatch(
                setSavedIds({
                  ...applicantsSavedIds,
                  bankDocLink: mainApplicant?.linkUrl,
                  secondBankDocLink: childApplicant?.linkUrl,
                })
              );

              const yourDetailsData = {
                ...applicationData.YourDetails,
                id: mainApplicant?.id,
                employment: {
                  ...applicationData.YourDetails.employment,
                  id: mainApplicant?.employment?.id,
                },
                liabilities: {
                  ...applicationData.YourDetails.liabilities,
                  id: mainApplicant?.liabilities?.id,
                },
                incomes: applicationData.YourDetails?.incomes?.map(
                  (item: any, index: number) => ({
                    ...item,
                    id: mainApplicant.incomes?.[index]?.id ?? item.id,
                  })
                ),
                expenses: yourExpensesData?.map((item: any, index: number) => ({
                  id: item.id,
                  type: item.type,
                  amount: Number(item.amount),
                })),
              };

              const coBorrowerDetailsData = {
                ...applicationData.Coborrower,
                id: childApplicant?.id,
                employment: {
                  ...applicationData.Coborrower.employment,
                  id: childApplicant?.employment?.id,
                },
                liabilities: {
                  ...applicationData.Coborrower.liabilities,
                  id: childApplicant?.liabilities?.id,
                },
                incomes: applicationData.Coborrower?.incomes?.map(
                  (item: any, index: number) => ({
                    ...item,
                    id: childApplicant.incomes?.[index]?.id ?? item.id,
                  })
                ),
                expenses: coExpensesData?.map((item: any, index: number) => ({
                  id: item.id,
                  type: item.type,
                  amount: Number(item.amount),
                })),
              };

              dispatch(
                addJointApplicationData({
                  category: "Coborrower",
                  data: coBorrowerDetailsData,
                })
              );
              dispatch(
                addJointApplicationData({
                  category: "YourDetails",
                  data: yourDetailsData,
                })
              );
            }
            reset({ otp: "" });
          },
          onError: (err: any) => {
            const message =
              err?.response?.data?.error?.toString() ??
              err?.response?.data?.message?.toString() ??
              err?.message?.toString();
            if (message === "Invalid OTP") {
              toast.error("Please enter correct OTP");
            } else {
              handleApiError(err);
            }
          },
        }
      );
    }
  };

  const handleResend = () => {
    if (applicantsSavedIds?.applicantID && timer === 0) {
      ResendOTPMutate(
        { id: applicantsSavedIds?.applicantID },
        {
          onSuccess: (data) => {
            setTimer(30);
            toast.success("OTP has been resent successfully");
            setTimer(30);
            setIsTimerVisible(true);
          },
          onError: (err: any) => {
            toast.error(
              err?.response?.data?.message || "something went wrong."
            );
          },
        }
      );
    }
  };

  useEffect(() => {
    if (
      serviceabilityData &&
      serviceabilityData?.ServiceabilityResults?.length > 0
    ) {
      const netSurplus =
        serviceabilityData?.ServiceabilityResults[0]?.ApplicationServiceability
          ?.NetSurplusRatioExcludingAddbacks;
      const MaxBorrowingAmount =
        serviceabilityData?.ServiceabilityResults[0]?.ApplicationServiceability
          ?.MaxBorrowingAmount;

      dispatch(setServiceability({ maxBorrowingAmount: MaxBorrowingAmount }));
      if (netSurplus > 0) {
        dispatch(
          setSavedIds({
            ...applicantsSavedIds,
            applicationStatus: "approved",
          })
        );
        dispatch(nextStep());
      } else if (netSurplus < 0) {
        // if the application is failed
        dispatch(
          setSavedIds({
            ...applicantsSavedIds,
            applicationStatus: "declined",
          })
        );
        dispatch(setStep(isRefinance ? 18 : 17));
      } else {
        console.log("The Net Surplus is zero.");
      }
    }
  }, [serviceabilityData]);

  useEffect(() => {
    if (
      applicantsSavedIds?.applicationStatus === "declined" &&
      applicantsSavedIds?.loanApplicationID
    ) {
      refetch();
    }
  }, [applicantsSavedIds]);

  return (
    <>
      {(isPending || isLoading || statusIsPending) && (
        <Loader text="Please wait while we are calculating serviceability" />
      )}
      <ProgressBar progress={100} />
      <div className="w-full  sm:w-[70%] xl:w-[40%]  mx-auto text-center px-4 sm:px-0">
        <MainTitle title="OTP" />
        <SubTitle
          title={`Please enter the code we have sent to ${maskPhoneNumber(
            applicants
              ? applicants?.phoneNumber
              : JointapplicationData
              ? JointapplicationData?.YourDetails?.phoneNumber
              : ""
          )}`}
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-center otpInputs">
            <Controller
              name="otp"
              control={control}
              rules={{
                required: "OTP is required",
                minLength: { value: 4, message: "OTP must be 4 digits" },
              }}
              render={({ field }) => (
                <OtpInput
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  numInputs={4}
                  inputStyle={{
                    width: "75px",
                    height: "65px",
                    margin: "0 7px",
                    borderRadius: "8px",
                    border: `${
                      errors.otp ? "1px solid red" : "1px solid #D9D9D9"
                    }`,
                    textAlign: "center",
                    fontSize: "18px",
                    backgroundColor: "#fff",
                    transition: "all 0.2s ease-in-out",
                  }}
                  renderInput={(props) => <input {...props} />}
                />
              )}
            />
          </div>
          <div className="mt-4 sm:mt-5">
            <button
              type="button"
              className={`text-base font-Mulish font-semibold mt-4 ${
                isPending || resedisPending || timer > 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gold"
              }`}
              disabled={isPending || resedisPending || timer > 0}
              onClick={handleResend}
            >
              {resedisPending ? (
                "Loading..."
              ) : (
                <>
                  Resend OTP{" "}
                  {isTimerVisible && timer > 0 && (
                    <span className="text-gold text-[16px] mt-2">
                      00:{timer.toString().padStart(2, "0")}
                    </span>
                  )}
                </>
              )}
            </button>

            {errors.otp && <ErrorsMessage title={errors.otp?.message} />}
          </div>
          <div className="flex justify-between my-10">
            <PreviousButtom type="button" onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton
              type="submit"
              loading={isPending}
              // disabled={isTimerVisible && timer > 0}
            >
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default OTP;
